/**
 * RescheduleBookingDialog — Phase 178.6.F.
 *
 * Premium shadcn replacement for the `window.prompt()` reschedule flow.
 * Datetime picker, optional duration override, conflict-safe submit.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CalendarClock, ArrowRight, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { track } from '@/lib/analytics';

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function durationMinutes(startIso, endIso) {
  return Math.round((new Date(endIso) - new Date(startIso)) / 60000);
}

export default function RescheduleBookingDialog({ booking, open, onClose, onSuccess }) {
  const [newStart, setNewStart] = useState('');
  const [newDuration, setNewDuration] = useState(60);
  const [keepDuration, setKeepDuration] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (booking && open) {
      setNewStart(toLocalInput(booking.start_at));
      setNewDuration(durationMinutes(booking.start_at, booking.end_at) || 60);
      setKeepDuration(true);
      track('dialog_opened', { dialog: 'reschedule_booking', booking_id: booking.id });
    }
  }, [booking, open]);

  const handleSubmit = async () => {
    if (!newStart) { toast.error('Pick a new start time'); return; }
    const start = new Date(newStart);
    if (Number.isNaN(start.getTime())) { toast.error('Invalid start time'); return; }
    const payload = { new_start_at: start.toISOString() };
    if (!keepDuration) {
      const mins = Number(newDuration);
      if (!Number.isFinite(mins) || mins <= 0) { toast.error('Duration must be a positive integer'); return; }
      payload.new_end_at = new Date(start.getTime() + mins * 60000).toISOString();
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/arenax/bookings/${booking.id}/reschedule`, payload);
      track('dialog_submitted', { dialog: 'reschedule_booking', booking_id: booking.id, idempotent: r.data?.idempotent });
      toast.success(r.data?.idempotent ? 'No change — same window' : 'Rescheduled');
      onSuccess?.(r.data);
      onClose?.();
    } catch (err) {
      track('dialog_failed', { dialog: 'reschedule_booking', booking_id: booking.id, error: err?.response?.status });
      toast.error(err?.response?.data?.detail || 'Reschedule failed');
    } finally { setSubmitting(false); }
  };

  const handleCancel = () => {
    track('dialog_cancelled', { dialog: 'reschedule_booking', booking_id: booking?.id });
    onClose?.();
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" /> Reschedule booking
          </DialogTitle>
          <DialogDescription>
            Move <strong>{booking.player_name || 'this booking'}</strong> to a different time window.
            The player will be notified after you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider">Current</Label>
            <div className="text-sm font-mono mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {new Date(booking.start_at).toLocaleString()}
              {' → '}
              {new Date(booking.end_at).toLocaleString()}
            </div>
          </div>

          <div>
            <Label htmlFor="reschedule-start" className="text-xs uppercase tracking-wider">
              New start
            </Label>
            <Input
              id="reschedule-start"
              type="datetime-local"
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              className="mt-1"
              data-testid="reschedule-dialog-start-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={keepDuration}
                onChange={(e) => setKeepDuration(e.target.checked)}
                data-testid="reschedule-dialog-keep-duration"
              />
              Keep original duration ({durationMinutes(booking.start_at, booking.end_at) || 0} min)
            </label>
            {!keepDuration && (
              <div>
                <Label htmlFor="reschedule-duration" className="text-xs uppercase tracking-wider">
                  New duration (minutes)
                </Label>
                <Input
                  id="reschedule-duration"
                  type="number"
                  min={15}
                  step={15}
                  value={newDuration}
                  onChange={(e) => setNewDuration(e.target.value)}
                  className="mt-1"
                  data-testid="reschedule-dialog-duration-input"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={submitting}
                  data-testid="reschedule-dialog-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}
                  data-testid="reschedule-dialog-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
